import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SESSIONS } from "@/lib/sessions";
import { store } from "@/lib/storage";
import { playAmbient, stopAmbient, setVolume, isAudioSupported } from "@/lib/audio";
import { setTtsEnabled, speak, stopSpeaking, isTtsSupported, initTts } from "@/lib/tts";
import { shareSession } from "@/lib/share";
import { checkAccess } from "@/lib/entitlement";
import BreathingOrb from "@/components/BreathingOrb";
import { Play, Pause, Square, Volume2, X, Moon, Mic, MicOff, Share2 } from "lucide-react";
import { toast } from "sonner";

function fmt(sec) {
  sec = Math.max(0, Math.floor(sec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Player() {
  const nav = useNavigate();
  const { id } = useParams();
  const session = SESSIONS.find((s) => s.id === id);

  const [playing, setPlaying] = useState(false);
  const [remaining, setRemaining] = useState((session?.minutes || 0) * 60);
  const [vol, setVol] = useState(0.6);
  const [dim, setDim] = useState(false);
  const [error, setError] = useState(null);
  const [preScore, setPreScore] = useState(null);
  const [postScore, setPostScore] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [completedNaturally, setCompletedNaturally] = useState(false);
  const [scriptLine, setScriptLine] = useState("");
  const [tts, setTts] = useState(() => store.getPrefs().tts || false);
  const [gate, setGate] = useState({ status: "checking" });
  const lastLineRef = useRef("");
  const startedRef = useRef(null);
  const tickRef = useRef(null);
  const historyAtRef = useRef(null);

  useEffect(() => {
    initTts();
    if (session) {
      (async () => {
        const res = await checkAccess("session", session.id);
        setGate(res.allowed ? { status: "ok" } : { status: "locked", reason: res.reason });
      })();
    }
    return () => {
      stopAmbient({ fade: false });
      stopSpeaking();
      if (tickRef.current) clearInterval(tickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Script synchronisé + lecture vocale si activée
  useEffect(() => {
    if (!playing || !session || !session.script) return;
    const totalSec = session.minutes * 60;
    const elapsed = totalSec - remaining;
    const current = [...session.script].reverse().find((l) => l.t <= elapsed);
    if (current && current.text !== lastLineRef.current) {
      lastLineRef.current = current.text;
      setScriptLine(current.text);
      if (tts) speak(current.text);
    }
  }, [remaining, playing, session, tts]);

  function toggleTts() {
    const next = !tts;
    setTts(next);
    setTtsEnabled(next);
    const prefs = store.getPrefs();
    store.setPrefs({ ...prefs, tts: next });
    if (!next) stopSpeaking();
  }

  if (!session) {
    return (
      <div className="pt-10 text-center">
        <p className="text-[color:var(--ivoire)]">Séance introuvable.</p>
        <button className="mt-4 underline text-[color:var(--sauge-light)]" onClick={() => nav("/")}>Retour</button>
      </div>
    );
  }

  if (gate.status === "locked") {
    return (
      <div className="fixed inset-0 z-50 bg-[color:var(--bg-deep)] flex flex-col items-center justify-center p-6" data-testid="player-paywall">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--sauge-light)]">Séance Premium</p>
        <h1 className="mt-3 font-serif text-3xl text-[color:var(--ivoire)] text-center">{session.title}</h1>
        <p className="mt-3 text-sm text-[color:var(--ivoire-muted)] text-center max-w-xs">
          {gate.reason === "auth_required" ? "Connectez-vous pour débloquer cette séance." : "Passez Premium pour accéder à cette séance."}
        </p>
        <div className="mt-8 grid gap-3 w-full max-w-xs">
          {gate.reason === "auth_required" ? (
            <button data-testid="paywall-login" onClick={() => nav("/connexion", { state: { from: window.location.pathname } })}
              className="h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium">Se connecter</button>
          ) : (
            <button data-testid="paywall-upgrade" onClick={() => nav("/premium")}
              className="h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium">Voir les offres Premium</button>
          )}
          <button data-testid="paywall-back" onClick={() => nav(-1)}
            className="h-12 rounded-2xl border border-[color:var(--border)] text-[color:var(--ivoire)]">Retour</button>
        </div>
      </div>
    );
  }

  if (gate.status === "checking") {
    return <div className="fixed inset-0 z-50 bg-[color:var(--bg-deep)] flex items-center justify-center text-[color:var(--ivoire-muted)]">…</div>;
  }

  async function handleStart() {
    if (!isAudioSupported()) {
      setError("L'audio n'est pas disponible dans ce navigateur.");
      return;
    }
    const prefs = store.getPrefs();
    const res = await playAmbient(session.ambient, vol, { preferRecorded: prefs.preferRecorded });
    if (!res.ok) {
      if (res.error === "recorded-missing") {
        setError("Enregistrement demandé indisponible pour cette ambiance. Ajoutez le fichier dans /public/audio/ ou désactivez le mode enregistrement dans les Réglages.");
      } else {
        setError("Impossible de démarrer l'audio. Vérifiez les autorisations.");
      }
      return;
    }
    setError(null);
    setPlaying(true);
    if (tts) setTtsEnabled(true);
    startedRef.current = Date.now();
    const totalSec = session.minutes * 60;
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          finish(true);
          return 0;
        }
        // Fondu progressif dans le dernier tiers pour les séances marquées fadeOut
        if (session.fadeOut) {
          const elapsed = totalSec - r;
          const fadeStart = Math.floor(totalSec * 0.66);
          if (elapsed >= fadeStart) {
            const progress = (elapsed - fadeStart) / (totalSec - fadeStart);
            const targetVol = vol * Math.max(0, 1 - progress);
            setVolume(targetVol);
          }
        }
        return r - 1;
      });
    }, 1000);
  }

  function handlePause() {
    stopAmbient({ fade: true, fadeSeconds: 1 });
    stopSpeaking();
    setPlaying(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }

  function handleStop() {
    finish(false);
  }

  function finish(completedNaturally) {
    stopAmbient({ fade: true, fadeSeconds: 1.5 });
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setPlaying(false);
    setCompleted(true);
    setCompletedNaturally(completedNaturally);
    const totalSec = session.minutes * 60;
    const durationDone = completedNaturally ? session.minutes : Math.max(0, Math.round((totalSec - remaining) / 60));
    historyAtRef.current = store.addHistoryEntry({
      sessionId: session.id,
      title: session.title,
      minutes: session.minutes,
      durationDone,
      completed: completedNaturally,
      preScore,
      postScore: null,
    });
    if (completedNaturally) toast.success("Séance terminée. Doucement.");
    else toast("Séance interrompue.");
    stopSpeaking();
  }

  function handlePostScore(n) {
    setPostScore(n);
    if (historyAtRef.current) {
      store.updateHistoryEntry(historyAtRef.current, { postScore: n });
    }
  }

  async function handleShare() {
    const res = await shareSession(session);
    if (res.ok && res.mode === "clipboard") toast.success("Lien copié. Partagez-le à un proche.");
    else if (!res.ok && res.mode === "unsupported") toast.error("Partage non pris en charge par ce navigateur.");
  }

  // Script synchronisé - déplacé au-dessus

  function onVolume(v) {
    setVol(v);
    setVolume(v);
  }

  const elapsed = session.minutes * 60 - remaining;
  const progress = (elapsed / (session.minutes * 60)) * 100;

  return (
    <div
      data-testid="player-screen"
      className={`fixed inset-0 z-50 flex flex-col ${dim ? "bg-black" : "bg-[color:var(--bg-deep)]"} transition-colors`}
      style={{ backgroundImage: dim ? "none" : "radial-gradient(ellipse at top, rgba(136,160,150,0.08), transparent 60%)" }}
    >
      <div className="flex items-center justify-between px-5 pt-6 safe-top">
        <button
          data-testid="player-close-btn"
          onClick={() => { stopAmbient({ fade: false }); nav(-1); }}
          className="w-10 h-10 rounded-full border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire)]"
          aria-label="Fermer la séance"
        >
          <X size={18} />
        </button>
        <p className="font-mono text-[11px] tracking-widest uppercase text-[color:var(--sauge-light)]">
          {session.minutes} min · {session.method}
        </p>
        <div className="flex items-center gap-2">
          <button
            data-testid="player-share-btn"
            onClick={handleShare}
            className="w-10 h-10 rounded-full border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire)]"
            aria-label="Partager la séance"
            title="Envoyer à un proche (aucune donnée exposée)"
          >
            <Share2 size={18} />
          </button>
          {isTtsSupported() && session.script && session.script.length > 0 && (
            <button
              data-testid="player-tts-btn"
              onClick={toggleTts}
              className={`w-10 h-10 rounded-full border border-[color:var(--border)] flex items-center justify-center ${tts ? "text-[color:var(--sauge-light)] bg-[color:var(--sauge)]/15" : "text-[color:var(--ivoire)]"}`}
              aria-label={tts ? "Désactiver la voix" : "Activer la voix"}
              title="Voix guidée en français (si disponible dans le navigateur)"
            >
              {tts ? <Mic size={18} /> : <MicOff size={18} />}
            </button>
          )}
          <button
            data-testid="player-dim-btn"
            onClick={() => setDim(!dim)}
            className="w-10 h-10 rounded-full border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire)]"
            aria-label="Mode écran sombre"
            title="Mode écran sombre (ne garantit pas la lecture après verrouillage)"
          >
            <Moon size={18} className={dim ? "text-[color:var(--sauge-light)]" : ""} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {!completed && (
          <>
            <h1 className="font-serif text-2xl text-[color:var(--ivoire)] text-center mb-6">{session.title}</h1>

            {session.method === "breathing" && session.breath ? (
              <BreathingOrb rhythm={session.breath} active={playing} size={260} />
            ) : (
              <div className="w-[260px] h-[260px] rounded-full border border-[color:var(--border)] flex items-center justify-center animate-soft-pulse" data-testid="ambient-orb">
                <div className="w-32 h-32 rounded-full bg-[color:var(--sauge)]/25 backdrop-blur" />
              </div>
            )}

            {preScore === null && !playing && (
              <div className="mt-8 w-full max-w-sm" data-testid="pre-score">
                <p className="text-xs text-[color:var(--ivoire-muted)] mb-2 text-center">
                  Ressenti avant (facultatif, observation personnelle — 0 à 10)
                </p>
                <div className="grid grid-cols-11 gap-1">
                  {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                    <button
                      key={n}
                      data-testid={`pre-score-${n}`}
                      onClick={() => setPreScore(n)}
                      className="h-9 rounded-md border border-[color:var(--border)] text-xs text-[color:var(--ivoire-muted)] hover:bg-[#1B2A3E]"
                    >{n}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 font-mono text-4xl text-[color:var(--ivoire)]" data-testid="player-time">{fmt(remaining)}</div>
            <div className="w-64 h-1 bg-[color:var(--bg-surface)] rounded-full overflow-hidden mt-3">
              <div className="h-full bg-[color:var(--sauge)]" style={{ width: `${progress}%`, transition: "width 1s linear" }} />
            </div>

            {scriptLine && playing && (
              <p data-testid="player-script" className="mt-6 text-center text-[color:var(--ivoire-muted)] text-base max-w-xs leading-relaxed animate-fade-in" key={scriptLine}>
                {scriptLine}
              </p>
            )}

            {error && (
              <p data-testid="player-error" className="mt-4 text-sm text-amber-400 text-center">{error}</p>
            )}

            <div className="mt-10 flex items-center gap-4">
              {!playing ? (
                <button
                  data-testid="player-play-btn"
                  onClick={handleStart}
                  className="w-20 h-20 rounded-full bg-[color:var(--sauge)] text-[color:var(--bg-deep)] flex items-center justify-center hover:bg-[color:var(--sauge-light)] transition shadow-lg"
                  aria-label="Démarrer la séance"
                >
                  <Play size={28} fill="currentColor" />
                </button>
              ) : (
                <button
                  data-testid="player-pause-btn"
                  onClick={handlePause}
                  className="w-20 h-20 rounded-full bg-[color:var(--sauge)] text-[color:var(--bg-deep)] flex items-center justify-center hover:bg-[color:var(--sauge-light)] transition shadow-lg"
                  aria-label="Pause"
                >
                  <Pause size={28} fill="currentColor" />
                </button>
              )}
              <button
                data-testid="player-stop-btn"
                onClick={handleStop}
                className="w-14 h-14 rounded-full border border-[color:var(--border)] text-[color:var(--ivoire)] flex items-center justify-center"
                aria-label="Arrêter"
              >
                <Square size={20} />
              </button>
            </div>

            <div className="w-full max-w-xs mt-8 flex items-center gap-3">
              <Volume2 size={16} className="text-[color:var(--ivoire-muted)]" />
              <input
                data-testid="player-volume"
                type="range"
                min="0" max="1" step="0.01"
                value={vol}
                onChange={(e) => onVolume(parseFloat(e.target.value))}
                className="flex-1 accent-[color:var(--sauge)]"
                aria-label="Volume"
              />
            </div>

            <p className="mt-6 text-[11px] text-[color:var(--ivoire-subtle)] text-center max-w-xs">
              Prenez le temps qu'il vous faut.
            </p>
          </>
        )}

        {completed && (
          <div className="w-full max-w-sm text-center" data-testid="post-screen">
            <h2 className="font-serif text-3xl text-[color:var(--ivoire)]" data-testid="post-title">
              {completedNaturally ? "Séance terminée" : "Séance interrompue"}
            </h2>
            <p className="mt-3 text-sm text-[color:var(--ivoire-muted)]">
              {completedNaturally
                ? "Prenez un instant, sans transition brutale."
                : "Vous pourrez la reprendre quand vous voudrez."}
            </p>

            <p className="mt-8 text-xs text-[color:var(--ivoire-muted)] mb-2">
              Ressenti après (facultatif — 0 à 10)
            </p>
            <div className="grid grid-cols-11 gap-1">
              {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                <button
                  key={n}
                  data-testid={`post-score-${n}`}
                  onClick={() => handlePostScore(n)}
                  className={`h-9 rounded-md border text-xs ${
                    postScore === n
                      ? "bg-[color:var(--sauge)] text-[color:var(--bg-deep)] border-[color:var(--sauge)]"
                      : "border-[color:var(--border)] text-[color:var(--ivoire-muted)]"
                  }`}
                >{n}</button>
              ))}
            </div>

            <button
              data-testid="post-done-btn"
              onClick={() => nav("/")}
              className="mt-10 w-full h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium"
            >
              Revenir à l'accueil
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
